import React, { useState, useEffect } from "react";
import { Box, Popper, Typography, Avatar } from "@mui/material";
import { getAvatarColor, getAvatarInitials } from "../../utils/utils";

interface Subscriber {
  type: "user" | "space";
  name: string;
  email?: string;
  picture?: string | null;
  id?: string;
  profileOrgRelationshipId?: string;
}

interface SubscribersHoverPopperWrapperProps {
  subscribers: Subscriber[];
  userSubscribers: Subscriber[];
  spaceSubscribers: Subscriber[];
  children: (
    isDropdownOpen: boolean,
    setIsDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>,
    disableTooltip: boolean
  ) => React.ReactNode;
}

const SubscribersHoverPopperWrapper: React.FC<SubscribersHoverPopperWrapperProps> = ({
  subscribers,
  userSubscribers,
  spaceSubscribers,
  children,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (isDropdownOpen) setAnchorEl(null);
  }, [isDropdownOpen]);

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (subscribers.length > 1 && !isDropdownOpen) {
      setAnchorEl(e.currentTarget);
    }
  };

  const handleMouseLeave = () => setAnchorEl(null);

  const open = Boolean(anchorEl);
  const disableTooltip = subscribers.length > 1 && open;

  return (
    <>
      <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children(isDropdownOpen, setIsDropdownOpen, disableTooltip)}
      </Box>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          { name: "flip", enabled: true },
          { name: "offset", options: { offset: [0, 8] } },
        ]}
        sx={{ zIndex: 1300, pointerEvents: "auto" }}
      >
        <Box
          sx={{
            p: 1.5,
            backgroundColor: "#fff",
            borderRadius: "6px",
            boxShadow: 3,
            minWidth: 220,
          }}
        >
            {spaceSubscribers.length > 0 && (
            <>
              <Typography
                variant="caption"
                sx={{ fontSize: 12,
                      fontWeight: 600,
                      color: "#777", }}
              >
                Spaces
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {spaceSubscribers.map((space, idx) => (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      variant="square"
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: getAvatarColor(space.name || ""),
                        fontSize: 12,
                      }}
                    >
                      {getAvatarInitials(space.name || "")}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {space.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
          {/* PEOPLE */}
          {userSubscribers.length > 0 && (
            <>
              <Typography variant="caption" 
                    sx={{ 
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#777", }}>
                People
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                {userSubscribers.map((user, idx) => (
                  <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      src={user.picture || undefined}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: !user.picture
                          ? getAvatarColor(user.email || user.name || "")
                          : undefined,
                        fontSize: "12px",
                      }}
                    >
                      {!user.picture && getAvatarInitials(user.email || user.name || "")}
                    </Avatar>
                    <Typography variant="body2" noWrap>
                      {user.name}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </Popper>
    </>
  );
};

export default SubscribersHoverPopperWrapper;
